/**
 * API base URL for Enterprise Salon Admin backend requests.
 * Must NOT reuse REACT_APP_SALON_ADMIN_API_URL — that points at the Nyafaz salon-admin API.
 */
const DEFAULT_DEV_API_ORIGIN = 'http://localhost:5013';
const PRODUCTION_CUSTOMER_HOST = 'analyze.nyafaz.com';
const PRODUCTION_ENTERPRISE_API_PATH = '/enterprise-salon-admin-api';

function isLocalHostname(host) {
  const h = String(host || '').toLowerCase();
  return (
    h === 'localhost' ||
    h === '127.0.0.1' ||
    h === '[::1]' ||
    h.endsWith('.local')
  );
}

/** Same-origin nginx proxy as makeup-hair (/enterprise-makeup-api). */
function productionEnterpriseApiOriginForHost(origin) {
  try {
    const url = new URL(origin);
    const host = String(url.hostname || '').toLowerCase();
    if (isLocalHostname(host)) return null;
    return `${url.origin}${PRODUCTION_ENTERPRISE_API_PATH}`;
  } catch {
    // ignore invalid origin
  }
  return null;
}

function isEnterpriseSalonAdminDevOrigin(origin) {
  if (!origin) return false;
  try {
    const { hostname, port } = new URL(origin);
    const isLocalHost =
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '[::1]' ||
      hostname.endsWith('.local');
    return isLocalHost && (port === '3000' || port === '3006' || port === '3008' || port === '5173' || port === '');
  } catch {
    return false;
  }
}

function normalizeApiBase(value) {
  if (!value || !String(value).trim()) return '';
  return String(value).trim().replace(/\/$/, '');
}

/** Reject accidental Nyafaz salon-admin API bases when resolving Enterprise. */
function isWrongSalonAdminApi(url) {
  const text = String(url || '').toLowerCase();
  if (!text) return false;
  // Exact path segment for the non-Enterprise admin API
  if (text.includes('/salon-admin-api') && !text.includes('/enterprise-salon-admin-api')) {
    return true;
  }
  // Dev port for non-Enterprise salon-admin
  if (/:5003(\/|$)/.test(text)) return true;
  return false;
}

export function getSalonAdminApiBaseUrl() {
  const fromEnterpriseEnv = normalizeApiBase(
    typeof process !== 'undefined' && process.env && process.env.REACT_APP_ENTERPRISE_SALON_ADMIN_API_URL
  );
  if (fromEnterpriseEnv && !isWrongSalonAdminApi(fromEnterpriseEnv)) {
    return fromEnterpriseEnv;
  }

  if (typeof window !== 'undefined') {
    const fromWindow = normalizeApiBase(window.REACT_APP_ENTERPRISE_SALON_ADMIN_API_URL);
    if (fromWindow && !isWrongSalonAdminApi(fromWindow)) {
      return fromWindow;
    }

    const origin = window.location && window.location.origin;

    // Production host mapping must win over shared REACT_APP_SALON_ADMIN_API_URL
    // (that env var is for Nyafaz salon-admin, not Enterprise).
    if (origin) {
      const mapped = productionEnterpriseApiOriginForHost(origin);
      if (mapped) return mapped;
    }

    if (isEnterpriseSalonAdminDevOrigin(origin)) {
      return DEFAULT_DEV_API_ORIGIN;
    }
  }

  // Dev-only: allow REACT_APP_SALON_ADMIN_API_URL when scripts point it at 5013.
  const fromSalonEnv = normalizeApiBase(
    typeof process !== 'undefined' && process.env && process.env.REACT_APP_SALON_ADMIN_API_URL
  );
  if (fromSalonEnv && !isWrongSalonAdminApi(fromSalonEnv)) {
    return fromSalonEnv;
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    const mapped = productionEnterpriseApiOriginForHost(window.location.origin);
    if (mapped) return mapped;
  }

  return DEFAULT_DEV_API_ORIGIN;
}
